import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';

/**
 * EU-593 — modal "Busque por candidatos" (frame Management / Search). Input
 * nome/ID/e-mail; Buscar desabilitado com campo vazio, tooltip "Digite um
 * termo para fazer uma busca" (GPR-032/033). A busca é server-side e
 * submit-driven (sem debounce — regra server-side-search não se aplica).
 */
export interface CandidateSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Termo atualmente aplicado — pré-preenche ao reabrir. */
  currentTerm: string;
  onSearch: (term: string) => void;
}

export function CandidateSearchDialog({
  open,
  onOpenChange,
  currentTerm,
  onSearch,
}: CandidateSearchDialogProps) {
  const [term, setTerm] = useState(currentTerm);

  useEffect(() => {
    if (open) setTerm(currentTerm);
  }, [open, currentTerm]);

  const trimmed = term.trim();
  const canSearch = trimmed.length > 0;

  function submit() {
    if (!canSearch) return;
    onSearch(trimmed);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogHeader className="gap-4 px-6 pt-6 pb-6">
            <DialogTitle>Busque por candidatos</DialogTitle>
            <Input
              data-testid="program-candidates-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Busque por nome, ID ou e-mail"
              autoFocus
            />
          </DialogHeader>
          <DialogFooter className="px-6 pb-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Voltar
            </Button>
            {canSearch ? (
              <Button type="submit" data-testid="program-candidates-search-submit">
                Buscar
              </Button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="cursor-not-allowed">
                      <Button type="submit" data-testid="program-candidates-search-submit" disabled>
                        Buscar
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Digite um termo para fazer uma busca</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
